import { computed, effect, inject } from '@angular/core';
import { Project } from '@app/models/project.models';
import {
  ACTUALLY_LOADING_SATE,
  DEAFAULT_LOADING_STATE,
  LOADED_LOADING_STATE,
  LoadingState,
  PaginatedDataRequest,
  ProjectDataRequest,
  RELOADING_LOADING_STATE,
} from '@app/models/shared.models';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  removeAllEntities,
  removeEntity,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { ProjectAdminService } from 'src/app/+services/admin/project-admin.service';
import { ProjectService } from 'src/app/+services/projects/service';
import { AuthStore } from '../auth.store';

interface ProjectStateModel {
  projectDetails?: any;
  ongoingProjectLoadingState: LoadingState;
  preparedProjectLoadingState: LoadingState;
  closedProjectLoadingState: LoadingState;
  ownProjectLoadingState: LoadingState;
  ownInvestedProjectLoadingState: LoadingState;
  followedProjectLoadingState: LoadingState;
  ongoingProjectTotal: number;
  preparedProjectTotal: number;
  closedProjectTotal: number;
  ownProjectTotal: number;
  ownInvestedProjectTotal: number;
  followedProjectTotal: number;
  adminProjectLoadingState: LoadingState;
  adminProjectTotal: number;
}

export const ProjectsStore = signalStore(
  { providedIn: 'root' },
  withState<ProjectStateModel>(() => ({
    ongoingProjectLoadingState: DEAFAULT_LOADING_STATE,
    preparedProjectLoadingState: DEAFAULT_LOADING_STATE,
    closedProjectLoadingState: DEAFAULT_LOADING_STATE,
    ownProjectLoadingState: DEAFAULT_LOADING_STATE,
    ownInvestedProjectLoadingState: DEAFAULT_LOADING_STATE,
    followedProjectLoadingState: DEAFAULT_LOADING_STATE,
    ongoingProjectTotal: 0,
    preparedProjectTotal: 0,
    closedProjectTotal: 0,
    ownProjectTotal: 0,
    ownInvestedProjectTotal: 0,
    followedProjectTotal: 0,
    adminProjectLoadingState: DEAFAULT_LOADING_STATE,
    adminProjectTotal: 0,
  })),
  withEntities({ entity: type<Project>(), collection: 'ongoingProject' }),
  withEntities({ entity: type<Project>(), collection: 'preparedProject' }),
  withEntities({ entity: type<Project>(), collection: 'closedProject' }),
  withEntities({ entity: type<Project>(), collection: 'ownProject' }),
  withEntities({ entity: type<Project>(), collection: 'ownInvestedProject' }),
  withEntities({ entity: type<Project>(), collection: 'followedProject' }),
  withEntities({ entity: type<Project>(), collection: 'allFollowedProject' }),
  withEntities({ entity: type<Project>(), collection: 'adminProject' }),
  withComputed((state) => {
    return {
      adminLoadingState: computed(() => state.adminProjectLoadingState()),
    };
  }),
  withMethods((state) => {
    const projectService = inject(ProjectService);
    const projectAdminService = inject(ProjectAdminService);

    const updateOngoingProjects = (values: Project[]) =>
      patchState(state, addEntities(values, { collection: 'ongoingProject' }));
    const updatePreparedProjects = (values: Project[]) =>
      patchState(state, addEntities(values, { collection: 'preparedProject' }));
    const updateClosedProjects = (values: Project[]) =>
      patchState(state, addEntities(values, { collection: 'closedProject' }));

    const getProjectById = (projectId: string) =>
      projectService.getProjectById(projectId);

    const loadOngoingProjects = (request: ProjectDataRequest) => {
      patchState(state, {
        ongoingProjectLoadingState: state.ongoingProjectLoadingState().loaded
          ? RELOADING_LOADING_STATE
          : ACTUALLY_LOADING_SATE,
      });
      projectService.getActualProjects(request).subscribe((values) => {
        patchState(
          state,
          setAllEntities(values.data, { collection: 'ongoingProject' }),
          {
            ongoingProjectLoadingState: LOADED_LOADING_STATE,
            ongoingProjectTotal: values.rowCount,
          },
        );
      });
    };

    const loadPreparedProjects = (request: ProjectDataRequest) => {
      patchState(state, {
        preparedProjectLoadingState: state.preparedProjectLoadingState().loaded
          ? RELOADING_LOADING_STATE
          : ACTUALLY_LOADING_SATE,
      });
      projectService.getPreparedProjects(request).subscribe((values) => {
        patchState(
          state,
          setAllEntities(values.data, { collection: 'preparedProject' }),
          {
            preparedProjectLoadingState: LOADED_LOADING_STATE,
            preparedProjectTotal: values.rowCount,
          },
        );
      });
    };

    const loadClosedProjects = (request: ProjectDataRequest) => {
      patchState(state, {
        closedProjectLoadingState: state.closedProjectLoadingState().loaded
          ? RELOADING_LOADING_STATE
          : ACTUALLY_LOADING_SATE,
      });
      projectService.getClosedProjects(request).subscribe((values) => {
        patchState(
          state,
          setAllEntities(values.data, { collection: 'closedProject' }),
          {
            closedProjectLoadingState: LOADED_LOADING_STATE,
            closedProjectTotal: values.rowCount,
          },
        );
      });
    };

    const loadAdminProjects = (request: ProjectDataRequest) => {
      patchState(state, {
        adminProjectLoadingState: state.adminProjectLoadingState().loaded
          ? RELOADING_LOADING_STATE
          : ACTUALLY_LOADING_SATE,
      });
      projectAdminService.getProjectsAdmin(request).subscribe((values) => {
        patchState(
          state,
          setAllEntities(values.data, { collection: 'adminProject' }),
          {
            adminProjectLoadingState: LOADED_LOADING_STATE,
            adminProjectTotal: values.rowCount,
          },
        );
      });
    };

    const addFollowedProject = (project: Project) => {
      patchState(
        state,
        addEntities([project], { collection: 'allFollowedProject' }),
      );
    };

    const removeFollowedProject = (projectId: string) => {
      patchState(
        state,
        removeEntity(projectId, { collection: 'allFollowedProject' }),
      );
    };

    return {
      updateOngoingProjects: updateOngoingProjects,
      updatePreparedProjects: updateOngoingProjects,
      updateClosedProjects: updateOngoingProjects,
      getProjectById,
      loadOngoingProjects,
      loadPreparedProjects,
      loadClosedProjects,
      loadAdminProjects,
      addFollowedProject,
      removeFollowedProject,
      loadOwnProjects: (request: PaginatedDataRequest) => {
        patchState(state, {
          ownProjectLoadingState: state.ownProjectLoadingState().loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService.getOwnProjects(request).subscribe((values) => {
          patchState(
            state,
            setAllEntities(values.data, { collection: 'ownProject' }),
            {
              ownProjectLoadingState: LOADED_LOADING_STATE,
              ownProjectTotal: values.rowCount,
            },
          );
        });
      },
      loadOwnInvestedProjects: (request: PaginatedDataRequest) => {
        patchState(state, {
          ownInvestedProjectLoadingState: state.ownInvestedProjectLoadingState()
            .loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService.getOwnInvestedProjects(request).subscribe((values) => {
          patchState(
            state,
            setAllEntities(values.data, { collection: 'ownInvestedProject' }),
            {
              ownInvestedProjectLoadingState: LOADED_LOADING_STATE,
              ownInvestedProjectTotal: values.rowCount,
            },
          );
        });
      },
      loadFollowedProjects: (request: PaginatedDataRequest) => {
        patchState(state, {
          followedProjectLoadingState: state.followedProjectLoadingState()
            .loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService.getFollowedProjects(request).subscribe((values) => {
          patchState(
            state,
            setAllEntities(values.data, { collection: 'followedProject' }),
            {
              followedProjectLoadingState: LOADED_LOADING_STATE,
              followedProjectTotal: values.rowCount,
            },
          );
        });
      },
      loadAllFollowedProjects: () => {
        projectService.getAllFollowedProjects().subscribe((values) => {
          patchState(
            state,
            setAllEntities(values, { collection: 'allFollowedProject' }),
          );
        });
      },
    };
  }),
  withHooks((store) => {
    return {
      onInit() {
        const projectService = inject(ProjectService);
        const authStore = inject(AuthStore);
        patchState(store, {
          ongoingProjectLoadingState: store.ongoingProjectLoadingState().loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService
          .getActualProjects({ skip: 0, take: 10 })
          .subscribe((values) => {
            patchState(
              store,
              setAllEntities(values.data, { collection: 'ongoingProject' }),
              {
                ongoingProjectLoadingState: LOADED_LOADING_STATE,
                ongoingProjectTotal: values.rowCount,
              },
            );
          });
        patchState(store, {
          preparedProjectLoadingState: store.preparedProjectLoadingState()
            .loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService
          .getPreparedProjects({ skip: 0, take: 10 })
          .subscribe((values) => {
            patchState(
              store,
              setAllEntities(values.data, { collection: 'preparedProject' }),
              {
                preparedProjectLoadingState: LOADED_LOADING_STATE,
                preparedProjectTotal: values.rowCount,
              },
            );
          });
        patchState(store, {
          closedProjectLoadingState: store.closedProjectLoadingState().loaded
            ? RELOADING_LOADING_STATE
            : ACTUALLY_LOADING_SATE,
        });
        projectService
          .getClosedProjects({ skip: 0, take: 10 })
          .subscribe((values) => {
            patchState(
              store,
              setAllEntities(values.data, { collection: 'closedProject' }),
              {
                closedProjectLoadingState: LOADED_LOADING_STATE,
                closedProjectTotal: values.rowCount,
              },
            );
          });

        effect(() => {
          const isLoggedIn = authStore.isLoggedIn();
          const user = authStore.user();
          const company = authStore.loggedInCompany();

          if (isLoggedIn) {
            store.loadAllFollowedProjects();
          } else {
            patchState(
              store,
              removeAllEntities({ collection: 'allFollowedProject' }),
            );
          }
        });
      },
    };
  }),
);
